import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { tokens, subscribe } from "./cf_theme.js";
import {
  normalizeHermesResidentProfile,
  toolsForHermesCapabilities,
  buildHermesResidentFirstMessage,
} from "./hermes_resident.js";
import {
  RESIDENT_TRAY_MARGIN,
  RESIDENT_TRAY_PANEL_GAP,
  RESIDENT_TRAY_PANEL_MAX_WIDTH,
  RESIDENT_TRAY_SIZE,
  collectResidentTrayAvoidRects,
  computeResidentTrayPanelLayout,
  computeResidentTrayPlacement,
} from "./hermes_resident_tray_placement.js";
import { TERMINAL_STATUSES, computeCapWarning, formatCapWarningLabel } from "./cf_run_status.js";

function useThemeBump() {
  const [, bump] = useReducer((x) => x + 1, 0);
  useEffect(() => subscribe(() => bump()), []);
}

function authHeaders(extra = {}) {
  try {
    const token = localStorage.getItem("cf_auth_token") || "";
    return token ? { ...extra, Authorization: "Bearer " + token } : extra;
  } catch {
    return extra;
  }
}

async function api(path, opts = {}) {
  const headers = authHeaders({ ...(opts.headers || {}) });
  if (opts.body && !headers["Content-Type"]) headers["Content-Type"] = "application/json";
  const r = await fetch(path, { ...opts, headers, cache: "no-store" });
  let body = null;
  try { body = await r.json(); } catch { body = { error: "http_" + r.status }; }
  if (!r.ok) throw new Error(body.error || ("http_" + r.status));
  return body;
}

// After a resident submission, follow the run to a terminal state and tell
// the user what happened -- the tray itself only tracks ACTIVE runs, so a
// failed/timed-out run would otherwise just disappear with no explanation.
async function watchResidentRun(runId, showToast, onOpenHermes) {
  if (!runId || typeof showToast !== "function") return;
  const open = () => onOpenHermes && onOpenHermes(runId);
  const BAD = { failed: "failed", cancelled: "was cancelled", expired: "expired", capped_steps: "hit its step cap", capped_dollars: "hit its budget cap" };
  const deadline = Date.now() + 240000;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 4000));
    let d = null;
    try { d = await api("/api/hermes/runs?limit=10"); } catch { continue; }
    const run = (Array.isArray(d.runs) ? d.runs : []).find((r) => r.id === runId);
    if (!run) continue;
    if (run.status === "done") {
      showToast({ text: "Hermes finished your request", actionLabel: "Open", onAction: open });
      return;
    }
    if (BAD[run.status]) {
      const why = run.last_error ? (": " + run.last_error) : "";
      showToast({ text: "Hermes run " + BAD[run.status] + why, tone: "error", actionLabel: "Open", onAction: open });
      return;
    }
  }
  showToast({ text: "Hermes is still working -- open the panel to follow along.", actionLabel: "Open", onAction: open });
}

function shortSurface(section) {
  if (!section) return "platform";
  return String(section).charAt(0).toUpperCase() + String(section).slice(1);
}

function appendDictationText(current, addition) {
  const clean = String(addition || "").replace(/\s+/g, " ").trim();
  if (!clean) return current || "";
  const base = String(current || "");
  if (!base.trim()) return clean;
  return base.replace(/\s+$/, "") + " " + clean;
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const str = String(reader.result || "");
      const comma = str.indexOf(",");
      resolve(comma >= 0 ? str.slice(comma + 1) : str);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

const HERMES_ATTACHMENT_EXTENSIONS = Object.freeze([
  ".pdf",
  ".doc", ".docx", ".rtf", ".odt",
  ".ppt", ".pptx", ".odp",
  ".xls", ".xlsx", ".xlsm", ".xlsb", ".ods",
  ".csv", ".tsv", ".json", ".jsonl", ".ndjson",
  ".txt", ".text", ".md", ".markdown", ".log",
  ".xml", ".yaml", ".yml", ".toml", ".ini", ".conf",
  ".js", ".jsx", ".ts", ".tsx", ".py", ".rb", ".go", ".rs", ".java", ".kt", ".swift", ".c", ".cpp", ".h", ".hpp", ".cs", ".php", ".sh", ".sql", ".html", ".css", ".scss",
  ".zip",
  ".png", ".jpg", ".jpeg", ".webp", ".gif",
]);
const HERMES_ATTACHMENT_ACCEPT = HERMES_ATTACHMENT_EXTENSIONS.join(",");
const HERMES_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
const HERMES_ATTACHMENT_MAX_FILES = 6;
const HERMES_ATTACHMENT_TEXT_EXTENSIONS = new Set([
  "txt", "text", "md", "markdown", "csv", "tsv", "json", "jsonl", "ndjson",
  "xml", "yaml", "yml", "toml", "ini", "conf", "log",
  "js", "jsx", "ts", "tsx", "py", "rb", "go", "rs", "java", "kt", "swift", "c", "cpp", "h", "hpp", "cs", "php", "sh", "sql", "html", "css", "scss",
]);
const HERMES_ATTACHMENT_SPREADSHEET_EXTENSIONS = new Set(["xls", "xlsx", "xlsm", "xlsb", "ods"]);
const HERMES_ATTACHMENT_PRESENTATION_EXTENSIONS = new Set(["pptx", "odp"]);
const HERMES_ATTACHMENT_BUNDLE_EXTENSIONS = new Set(["zip"]);
const HERMES_ACTIVE_STATUSES = new Set(["pending", "running", "paused", "paused_user", "paused_budget"]);
const HERMES_RUN_POLL_STATUSES = "running,pending,paused,paused_user,paused_budget";
const _scriptPromises = new Map();

function formatAttachmentBytes(bytes) {
  if (!Number.isFinite(bytes)) return "";
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(bytes % (1024 * 1024) ? 1 : 0)}MB`;
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
}

function formatRunCost(cents) {
  const n = Number(cents) || 0;
  if (n <= 0) return "0c";
  if (n < 100) return `${n}c`;
  return `$${(n / 100).toFixed(n % 100 ? 2 : 0)}`;
}

function runStatusLabel(status) {
  const s = String(status || "pending");
  const labels = {
    pending: "PENDING",
    running: "RUNNING",
    paused: "PAUSED",
    paused_user: "PAUSED",
    paused_budget: "PAUSED",
    done: "DONE",
    failed: "FAILED",
    cancelled: "CANCELLED",
    expired: "EXPIRED",
    capped_steps: "CAPPED",
    capped_dollars: "CAPPED",
    capped_tools: "CAPPED",
    failed_length_check: "FAILED",
  };
  return labels[s] || s.toUpperCase();
}

function isActiveHermesRun(run) {
  return !!run && HERMES_ACTIVE_STATUSES.has(String(run.status || "pending"));
}

function isTerminalHermesRun(run) {
  return !!run && TERMINAL_STATUSES.has(String(run.status || ""));
}

function runProgressMeta(run) {
  if (!run) return "";
  const parts = [];
  const steps = Number(run.step_count) || 0;
  const stepCap = Number(run.step_cap) || 0;
  parts.push(stepCap > 0 ? `${steps}/${stepCap} steps` : `${steps} steps`);
  const tools = Number(run.tool_call_count) || 0;
  const toolCap = Number(run.tool_call_cap) || 0;
  if (tools || toolCap) parts.push(toolCap > 0 ? `${tools}/${toolCap} tools` : `${tools} tools`);
  const cost = Number(run.cost_cents_used) || 0;
  const costCap = Number(run.dollar_cap_cents) || 0;
  if (cost || costCap) parts.push(costCap > 0 ? `${formatRunCost(cost)}/${formatRunCost(costCap)}` : formatRunCost(cost));
  const warning = formatCapWarningLabel(computeCapWarning(run));
  if (warning) parts.push(warning);
  return parts.join(" · ");
}

function runFileName(file) {
  return String(file?.path || file?.filename || "Hermes file").split("/").pop() || "Hermes file";
}

function runFileDownloadLabel(file) {
  const ext = (runFileName(file).split(".").pop() || "").toLowerCase();
  if (ext === "docx") return "Download Word";
  if (ext === "pdf") return "Download PDF";
  if (ext === "pptx") return "Download Deck";
  if (ext === "md" || ext === "markdown") return "Download MD";
  if (ext === "xlsx" || ext === "csv") return "Download Data";
  return "Download";
}

function isGeneratedRunFile(file) {
  const p = String(file?.path || "").toLowerCase();
  if (!p) return false;
  if (p.startsWith("uploads/")) return false;
  if (p.endsWith(".notes.md")) return false;
  return true;
}

function prioritizeRunFiles(files) {
  const docExt = new Set(["docx", "pdf", "pptx", "md", "markdown", "xlsx", "csv", "zip"]);
  return (Array.isArray(files) ? files : [])
    .filter(isGeneratedRunFile)
    .sort((a, b) => {
      const ap = String(a.path || "");
      const bp = String(b.path || "");
      const ae = (ap.split(".").pop() || "").toLowerCase();
      const be = (bp.split(".").pop() || "").toLowerCase();
      const as = (ap.startsWith("artifacts/") ? 0 : 2) + (docExt.has(ae) ? 0 : 1);
      const bs = (bp.startsWith("artifacts/") ? 0 : 2) + (docExt.has(be) ? 0 : 1);
      return as - bs || ap.localeCompare(bp);
    });
}

function attachmentExt(file) {
  return (String(file?.name || "").split(".").pop() || "").toLowerCase();
}

function attachmentId() {
  try { return crypto.randomUUID(); } catch { return "att-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8); }
}

function loadExternalScript(src) {
  if (typeof window === "undefined") return Promise.reject(new Error("Browser runtime unavailable"));
  if (_scriptPromises.has(src)) return _scriptPromises.get(src);
  const p = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") return resolve();
      existing.addEventListener("load", resolve, { once: true });
      existing.addEventListener("error", () => reject(new Error("Could not load parser script")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => { script.dataset.loaded = "true"; resolve(); };
    script.onerror = () => reject(new Error("Could not load parser script"));
    document.head.appendChild(script);
  });
  _scriptPromises.set(src, p);
  return p;
}

async function loadJSZip() {
  await loadExternalScript("https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js");
  if (!window.JSZip) throw new Error("Word document parser failed to load");
  return window.JSZip;
}

function xmlText(xml) {
  return String(xml || "")
    .replace(/<w:tab\/>/g, "\t")
    .replace(/<\/w:p>/g, "\n")
    .replace(/<\/text:p>/g, "\n")
    .replace(/<\/text:h>/g, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

function arrayBufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

async function extractDocxTextFromBuffer(buf) {
  const JSZipCtor = await loadJSZip();
  const zip = await JSZipCtor.loadAsync(buf);
  const paths = [
    "word/document.xml",
    ...Object.keys(zip.files).filter((p) => /^word\/(header|footer|footnotes|endnotes)\d*\.xml$/i.test(p)),
  ];
  const parts = [];
  for (const p of paths) {
    const entry = zip.file(p);
    if (entry) parts.push(xmlText(await entry.async("text")));
  }
  return parts.filter(Boolean).join("\n\n");
}

async function extractOdtTextFromBuffer(buf) {
  const JSZipCtor = await loadJSZip();
  const zip = await JSZipCtor.loadAsync(buf);
  const entry = zip.file("content.xml");
  return entry ? xmlText(await entry.async("text")) : "";
}

async function extractPresentationTextFromBuffer(buf, ext) {
  const JSZipCtor = await loadJSZip();
  const zip = await JSZipCtor.loadAsync(buf);
  if (ext === "odp") {
    const entry = zip.file("content.xml");
    return entry ? xmlText(await entry.async("text")) : "";
  }
  const paths = Object.keys(zip.files)
    .filter((p) => /^ppt\/(slides|notesSlides)\/[^/]+\.xml$/i.test(p))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const parts = [];
  for (const p of paths) {
    const entry = zip.file(p);
    if (!entry) continue;
    const label = p.includes("/notesSlides/") ? "Notes" : "Slide";
    const num = (p.match(/(\d+)\.xml$/) || [])[1] || String(parts.length + 1);
    const text = xmlText(await entry.async("text"));
    if (text) parts.push(`# ${label} ${num}\n${text}`);
  }
  return parts.join("\n\n");
}

async function extractSpreadsheetTextFromBuffer(buf) {
  await loadExternalScript("https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js");
  const XLSX = window.XLSX;
  if (!XLSX) throw new Error("Spreadsheet parser failed to load");
  const wb = XLSX.read(buf, { type: "array", cellDates: true });
  const parts = [];
  for (const sheetName of wb.SheetNames || []) {
    const ws = wb.Sheets[sheetName];
    if (!ws) continue;
    const csv = XLSX.utils.sheet_to_csv(ws, { FS: "\t", RS: "\n", blankrows: false });
    if (csv.trim()) parts.push(`# Sheet: ${sheetName}\n${csv.trim()}`);
  }
  return parts.join("\n\n");
}

async function extractZipBundleTextFromBuffer(buf) {
  const JSZipCtor = await loadJSZip();
  const zip = await JSZipCtor.loadAsync(buf);
  const allPaths = Object.keys(zip.files).filter((p) => !zip.files[p]?.dir).sort();
  const readable = allPaths.filter((p) => {
    const lower = p.toLowerCase();
    if (/(^|\/)(node_modules|vendor|dist|build|coverage|\.git)\//.test(lower)) return false;
    const ext = (lower.split(".").pop() || "");
    return HERMES_ATTACHMENT_TEXT_EXTENSIONS.has(ext) || /(^|\/)(package\.json|requirements\.txt|readme|dockerfile|makefile)$/i.test(p);
  });
  const parts = [`Bundle contains ${allPaths.length} file${allPaths.length === 1 ? "" : "s"}.`, "", "Readable file index:", ...readable.slice(0, 80).map((p) => "- " + p)];
  let used = parts.join("\n").length;
  for (const p of readable.slice(0, 18)) {
    const entry = zip.file(p);
    if (!entry) continue;
    const text = await entry.async("text");
    const remaining = 24000 - used;
    if (remaining <= 800) break;
    const snippet = text.slice(0, Math.min(3200, remaining));
    parts.push(`\n\n## ${p}\n${snippet}${text.length > snippet.length ? "\n...(truncated)" : ""}`);
    used += snippet.length + p.length + 12;
  }
  return parts.join("\n");
}

async function extractPdfTextFromBuffer(buf) {
  await loadExternalScript("https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/legacy/build/pdf.min.js");
  if (window.pdfjsLib?.GlobalWorkerOptions) {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js";
  }
  if (!window.pdfjsLib) throw new Error("PDF parser failed to load");
  const pdf = await window.pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
  let text = "";
  for (let p = 1; p <= pdf.numPages; p += 1) {
    const page = await pdf.getPage(p);
    const tc = await page.getTextContent();
    text += tc.items.map((it) => it.str).join(" ") + "\n";
  }
  return { text: text.trim(), pages: pdf.numPages || 0 };
}

function binaryReadableTextFromBuffer(buf) {
  const raw = new TextDecoder("utf-8", { fatal: false }).decode(buf);
  return raw
    .replace(/\u0000/g, " ")
    .replace(/[^\x09\x0a\x0d\x20-\x7e]+/g, " ")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function imagePreviewDataUrl(file, maxSide = 900) {
  const dataUrl = await fileToDataUrl(file);
  return await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const w = img.naturalWidth || img.width || 1;
        const h = img.naturalHeight || img.height || 1;
        const scale = Math.min(1, maxSide / Math.max(w, h));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(w * scale));
        canvas.height = Math.max(1, Math.round(h * scale));
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      } catch {
        resolve(dataUrl.length < 900000 ? dataUrl : "");
      }
    };
    img.onerror = () => resolve("");
    img.src = dataUrl;
  });
}

async function prepareHermesAttachment(file) {
  const ext = attachmentExt(file);
  const extToken = ext ? "." + ext : "";
  const mime = file.type || "";
  const isImage = /^image\/(png|jpe?g|webp|gif)$/i.test(mime) || ["png", "jpg", "jpeg", "webp", "gif"].includes(ext);
  if (!HERMES_ATTACHMENT_EXTENSIONS.includes(extToken) && !isImage) {
    throw new Error(`Unsupported file type .${ext || "unknown"}`);
  }
  if (file.size > HERMES_ATTACHMENT_MAX_BYTES) {
    throw new Error(`Hermes workspace attachments are limited to ${formatAttachmentBytes(HERMES_ATTACHMENT_MAX_BYTES)} per file`);
  }
  const buf = await file.arrayBuffer();
  let extractedText = "";
  let extraction = "stored";
  let ocrRequested = false;
  let previewDataUrl = "";
  if (ext === "pdf" || mime === "application/pdf") {
    try {
      const pdf = await extractPdfTextFromBuffer(buf);
      extractedText = pdf.text || "";
      extraction = "pdfjs";
      if (pdf.pages > 0 && extractedText.length < pdf.pages * 24) {
        ocrRequested = true;
        extraction = "pdfjs_ocr_requested";
      }
    } catch {
      ocrRequested = true;
      extraction = "ocr_requested";
    }
  } else if (ext === "docx") {
    extractedText = await extractDocxTextFromBuffer(buf);
    extraction = "docx";
  } else if (ext === "odt") {
    extractedText = await extractOdtTextFromBuffer(buf);
    extraction = "odt";
  } else if (HERMES_ATTACHMENT_PRESENTATION_EXTENSIONS.has(ext)) {
    extractedText = await extractPresentationTextFromBuffer(buf, ext);
    extraction = ext;
  } else if (ext === "ppt") {
    extractedText = binaryReadableTextFromBuffer(buf);
    extraction = "legacy_ppt_text_guess";
  } else if (HERMES_ATTACHMENT_SPREADSHEET_EXTENSIONS.has(ext)) {
    extractedText = await extractSpreadsheetTextFromBuffer(buf);
    extraction = "spreadsheet";
  } else if (HERMES_ATTACHMENT_BUNDLE_EXTENSIONS.has(ext)) {
    extractedText = await extractZipBundleTextFromBuffer(buf);
    extraction = "code_bundle";
  } else if (ext === "rtf") {
    extractedText = new TextDecoder("utf-8", { fatal: false }).decode(buf)
      .replace(/\\par[d]?/g, "\n")
      .replace(/\\'[0-9a-fA-F]{2}/g, " ")
      .replace(/[{}]/g, " ")
      .replace(/\\[a-zA-Z]+\d* ?/g, " ")
      .replace(/[ \t]{2,}/g, " ")
      .trim();
    extraction = "rtf";
  } else if (ext === "doc") {
    extractedText = binaryReadableTextFromBuffer(buf);
    extraction = "legacy_doc_text_guess";
  } else if (isImage) {
    previewDataUrl = await imagePreviewDataUrl(file);
    extraction = previewDataUrl ? "image_preview" : "image_original_only";
  } else if (HERMES_ATTACHMENT_TEXT_EXTENSIONS.has(ext) || /^text\//i.test(mime) || /json|xml|yaml|csv/i.test(mime)) {
    extractedText = new TextDecoder("utf-8", { fatal: false }).decode(buf);
    extraction = "text";
  }
  return {
    id: attachmentId(),
    name: file.name || "attachment",
    mime: mime || "application/octet-stream",
    size: file.size,
    base64: arrayBufferToBase64(buf),
    extractedText,
    extraction,
    ocrRequested,
    previewDataUrl,
  };
}

function encodeRunFilePath(path) {
  return String(path || "").split("/").map(encodeURIComponent).join("/");
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export default function HermesResidentTray({ section, authUser, showToast, onOpenHermes, onOpenCode, getSurfaceContext, onTalkToMe }) {
  useThemeBump();
  const t = tokens();
  const [profile, setProfile] = useState(null);
  const [registeredTools, setRegisteredTools] = useState(null);
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("cowork");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [launcherHot, setLauncherHot] = useState(false);
  const [dictating, setDictating] = useState(false);
  const [dictationInterim, setDictationInterim] = useState("");
  const [dictationBusy, setDictationBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [downloadRuns, setDownloadRuns] = useState([]);
  const [downloadsLoading, setDownloadsLoading] = useState(false);
  const [downloadsError, setDownloadsError] = useState("");
  const [downloadBusy, setDownloadBusy] = useState("");
  const [deleteRunBusy, setDeleteRunBusy] = useState("");
  const [deleteRunErrors, setDeleteRunErrors] = useState({});
  const [dropHot, setDropHot] = useState(false);
  const [placement, setPlacement] = useState({ right: RESIDENT_TRAY_MARGIN, bottom: RESIDENT_TRAY_MARGIN, shifted: false });
  const [viewportBox, setViewportBox] = useState({ width: 0, height: 0 });
  const rootRef = useRef(null);
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);
  const recorderRef = useRef(null);
  const recorderStreamRef = useRef(null);
  const recorderTimerRef = useRef(null);
  const activeCount = runs.length;

  useEffect(() => {
    if (!authUser) return;
    let cancelled = false;
    api("/api/hermes/resident")
      .then((d) => {
        if (cancelled) return;
        setProfile(normalizeHermesResidentProfile(d.profile));
        setRegisteredTools(Array.isArray(d.registered_tools) ? d.registered_tools : null);
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      });
    return () => { cancelled = true; };
  }, [authUser?.id]);

  useEffect(() => {
    if (!authUser || !profile?.enabled) return;
    let cancelled = false;
    const tick = () => api("/api/hermes/runs?limit=5&status=" + HERMES_RUN_POLL_STATUSES)
      .then((d) => { if (!cancelled) setRuns(Array.isArray(d.runs) ? d.runs : []); })
      .catch(() => {});
    tick();
    const timer = setInterval(tick, 8000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [authUser?.id, profile?.enabled]);

  useEffect(() => {
    if (!open || !authUser || !profile?.enabled) return;
    let cancelled = false;
    let timer = 0;
    const load = async () => {
      setDownloadsLoading(true);
      setDownloadsError("");
      try {
        const [activeResp, recentResp] = await Promise.all([
          api("/api/hermes/runs?limit=5&status=" + HERMES_RUN_POLL_STATUSES),
          api("/api/hermes/runs?limit=8"),
        ]);
        if (cancelled) return;
        const activeRuns = Array.isArray(activeResp.runs) ? activeResp.runs : [];
        setRuns(activeRuns);
        const byId = new Map();
        for (const run of activeRuns) if (run?.id) byId.set(run.id, run);
        for (const run of (Array.isArray(recentResp.runs) ? recentResp.runs : [])) {
          if (run?.id && (isActiveHermesRun(run) || isTerminalHermesRun(run))) byId.set(run.id, run);
        }
        const cards = [];
        for (const run of Array.from(byId.values()).slice(0, 8)) {
          let files = [];
          let totalFiles = 0;
          try {
            const f = await api("/api/hermes/runs/" + encodeURIComponent(run.id) + "/files");
            totalFiles = Array.isArray(f.files) ? f.files.length : 0;
            files = prioritizeRunFiles(f.files);
          } catch {}
          cards.push({ run, files, totalFiles });
        }
        if (!cancelled) setDownloadRuns(cards);
      } catch (e) {
        if (!cancelled) {
          setDownloadRuns([]);
          setDownloadsError(e?.message || "run_status_unavailable");
        }
      } finally {
        if (!cancelled) setDownloadsLoading(false);
      }
    };
    load();
    timer = setInterval(load, 7000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [open, authUser?.id, profile?.enabled]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => () => {
    try { recognitionRef.current?.stop?.(); } catch {}
    try { recorderRef.current?.stop?.(); } catch {}
    try { recorderStreamRef.current?.getTracks?.().forEach((track) => track.stop()); } catch {}
    if (recorderTimerRef.current) clearTimeout(recorderTimerRef.current);
  }, []);

  const allowedTools = useMemo(
    () => profile ? toolsForHermesCapabilities(profile.capability_ids, registeredTools) : [],
    [profile, registeredTools]
  );
  const currentSurfaceEnabled = profile?.surfaces?.[section] !== false;
  const trayVisible = !!authUser && !!profile && !!profile.enabled && !!profile.show_global_tray && currentSurfaceEnabled;

  useEffect(() => {
    if (!trayVisible || typeof window === "undefined") return undefined;
    let frame = 0;
    const updatePlacement = () => {
      frame = 0;
      const viewport = window.visualViewport || {};
      const viewportWidth = viewport.width || window.innerWidth || 0;
      const viewportHeight = viewport.height || window.innerHeight || 0;
      const avoidRects = collectResidentTrayAvoidRects(rootRef.current, { viewportWidth, viewportHeight });
      const next = computeResidentTrayPlacement({ viewportWidth, viewportHeight, avoidRects });
      setViewportBox((prev) => (
        prev.width === viewportWidth && prev.height === viewportHeight
          ? prev
          : { width: viewportWidth, height: viewportHeight }
      ));
      setPlacement((prev) => (
        prev.right === next.right && prev.bottom === next.bottom && prev.shifted === next.shifted
          ? prev
          : { right: next.right, bottom: next.bottom, shifted: next.shifted }
      ));
    };
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(updatePlacement);
    };
    const observer = typeof MutationObserver !== "undefined" ? new MutationObserver(schedule) : null;
    observer?.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-expanded", "aria-hidden", "class", "hidden", "style"],
    });
    schedule();
    window.addEventListener("resize", schedule, { passive: true });
    window.addEventListener("scroll", schedule, { capture: true, passive: true });
    window.visualViewport?.addEventListener("resize", schedule, { passive: true });
    window.visualViewport?.addEventListener("scroll", schedule, { passive: true });
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, { capture: true });
      window.visualViewport?.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("scroll", schedule);
    };
  }, [activeCount, open, section, trayVisible]);

  if (!trayVisible) return null;

  const downloadRunZip = async (runId) => {
    if (!runId || downloadBusy) return;
    const busyId = "zip:" + runId;
    setDownloadBusy(busyId);
    try {
      const resp = await fetch("/api/hermes/runs/" + encodeURIComponent(runId) + "/export", { headers: authHeaders() });
      if (!resp.ok) throw new Error("http_" + resp.status);
      downloadBlob(await resp.blob(), "hermes-run-" + String(runId).slice(0, 8) + ".zip");
    } catch (e) {
      if (showToast) showToast("Download failed: " + (e?.message || e));
    } finally {
      setDownloadBusy("");
    }
  };

  const downloadRunFile = async (runId, file) => {
    if (!runId || !file?.path || downloadBusy) return;
    const busyId = runId + ":" + (file.id || file.path);
    setDownloadBusy(busyId);
    try {
      if (file.id) {
        const ticketResp = await fetch(
          "/api/hermes/runs/" + encodeURIComponent(runId) + "/files/" + encodeURIComponent(file.id) + "/download-ticket",
          { method: "POST", headers: authHeaders(), cache: "no-store" }
        );
        let ticket = null;
        try { ticket = await ticketResp.json(); } catch {}
        if (!ticketResp.ok) throw new Error(ticket?.error || ("http_" + ticketResp.status));
        if (ticket?.download_url) {
          try { window.location.assign(ticket.download_url); }
          catch { window.location.href = ticket.download_url; }
          return;
        }
      }
      const resp = await fetch("/api/hermes/runs/" + encodeURIComponent(runId) + "/files/" + encodeRunFilePath(file.path), { headers: authHeaders() });
      if (!resp.ok) throw new Error("http_" + resp.status);
      downloadBlob(await resp.blob(), String(file.path).split("/").pop() || "file");
    } catch (e) {
      if (showToast) showToast("Download failed: " + (e?.message || e));
    } finally {
      setDownloadBusy("");
    }
  };

  const deleteRun = async (run) => {
    const runId = run?.id;
    if (!runId || deleteRunBusy) return;
    if (!isTerminalHermesRun(run)) {
      setDeleteRunErrors((prev) => ({ ...prev, [runId]: "Runs can be deleted after they finish or are cancelled." }));
      return;
    }
    const ok = typeof window === "undefined" || window.confirm("Delete this Hermes run and its files? This cannot be undone.");
    if (!ok) return;
    setDeleteRunBusy(runId);
    setDeleteRunErrors((prev) => {
      const next = { ...prev };
      delete next[runId];
      return next;
    });
    try {
      await api("/api/hermes/runs/" + encodeURIComponent(runId), { method: "DELETE" });
      setDownloadRuns((prev) => prev.filter((card) => card?.run?.id !== runId));
      setRuns((prev) => prev.filter((item) => item?.id !== runId));
      if (showToast) showToast("Hermes run deleted.");
    } catch (e) {
      const msg = e?.message === "run_active"
        ? "Run is still active. Wait for it to finish, then delete it."
        : "Could not delete this run. Try again from Runs.";
      setDeleteRunErrors((prev) => ({ ...prev, [runId]: msg }));
    } finally {
      setDeleteRunBusy("");
    }
  };

  const launch = async () => {
    const rawGoal = (prompt || "").trim();
    const goal = mode === "code"
      ? ("Code mode request from the Hermes orb. Treat this as lightweight IDE/dev work; create scripts, code files, command notes, or app scaffolds as useful.\n\n" + rawGoal)
      : rawGoal;
    if (!goal || busy || attachmentBusy) return;
    if (attachments.length && (!allowedTools.includes("read_file") || !allowedTools.includes("list_files"))) {
      setError("Enable the resident Workspace files capability before submitting attachments.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      let memoryEntries = [];
      if (profile.learning?.use_user_memory) {
        try {
          const mem = await api("/api/memory");
          memoryEntries = Array.isArray(mem.entries) ? mem.entries : [];
        } catch {}
      }
      let surfaceContext = null;
      try {
        surfaceContext = typeof getSurfaceContext === "function" ? getSurfaceContext() : null;
      } catch {
        surfaceContext = null;
      }
      const run = await api("/api/hermes/runs", {
        method: "POST",
        body: JSON.stringify({
          goal,
          model_id: profile.model_id,
          step_cap: profile.default_caps.step_cap,
          dollar_cap_cents: profile.default_caps.dollar_cap_cents,
          tool_call_cap: profile.default_caps.tool_call_cap,
          allowed_tools: allowedTools,
          attachments: attachments.map((att) => ({
            name: att.name,
            mime: att.mime,
            size: att.size,
            base64: att.base64,
            extractedText: att.extractedText,
            extraction: att.extraction,
            ocrRequested: att.ocrRequested,
            previewDataUrl: att.previewDataUrl,
          })),
        }),
      });
      const first_message = buildHermesResidentFirstMessage({
        prompt: goal,
        surfaceContext,
        memoryEntries,
        profile,
        attachments: Array.isArray(run.attachments) ? run.attachments : [],
      });
      await api("/api/hermes/runs/" + encodeURIComponent(run.id) + "/start", {
        method: "POST",
        body: JSON.stringify({ first_message, surface_context: surfaceContext }),
      });
      setPrompt("");
      setAttachments([]);
      const optimisticRun = { ...run, status: "running" };
      setRuns((prev) => [optimisticRun, ...prev.filter((r) => r?.id !== run.id)].slice(0, 5));
      setDownloadRuns((prev) => [{ run: optimisticRun, files: [], totalFiles: 0 }, ...prev.filter((card) => card?.run?.id !== run.id)].slice(0, 6));
      watchResidentRun(run.id, showToast, onOpenHermes);
      if (showToast) {
        showToast({
          text: "Agent task submitted",
          actionLabel: "Open",
          onAction: () => {
            if (typeof onOpenHermes === "function") onOpenHermes();
            try { window.location.hash = "#hermes/run/" + encodeURIComponent(run.id); } catch {}
          },
        });
      }
    } catch (e) {
      setError(e.message || "launch_failed");
    } finally {
      setBusy(false);
    }
  };

  const addAttachments = async (files) => {
    const incoming = Array.from(files || []).filter(Boolean);
    if (!incoming.length || attachmentBusy) return;
    setAttachmentBusy(true);
    setDropHot(false);
    setError("");
    try {
      const remaining = Math.max(0, HERMES_ATTACHMENT_MAX_FILES - attachments.length);
      if (incoming.length > remaining) {
        if (showToast) showToast(`Hermes accepts up to ${HERMES_ATTACHMENT_MAX_FILES} attachments per request`);
      }
      const prepared = [];
      for (const file of incoming.slice(0, remaining)) {
        try {
          prepared.push(await prepareHermesAttachment(file));
        } catch (e) {
          const msg = `${file.name}: ${e.message || "could not attach"}`;
          if (showToast) showToast(msg);
          else setError(msg);
        }
      }
      if (prepared.length) setAttachments((prev) => [...prev, ...prepared]);
    } finally {
      setAttachmentBusy(false);
    }
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((att) => att.id !== id));
  };

  const finishMediaDictation = async (chunks, mime) => {
    setDictating(false);
    setDictationBusy(true);
    setDictationInterim("");
    try {
      const blob = new Blob(chunks, { type: mime || "audio/webm" });
      if (!blob.size) return;
      const audio = await blobToBase64(blob);
      const r = await fetch("/api/stt", {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ audio, mime: blob.type || mime || "audio/webm" }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setError(body.error === "stt_unavailable" ? "Voice transcription is not configured." : "Voice transcription failed.");
        return;
      }
      const d = await r.json().catch(() => ({}));
      if (d && d.text) setPrompt((prev) => appendDictationText(prev, d.text));
    } catch {
      setError("Voice transcription failed.");
    } finally {
      setDictationBusy(false);
    }
  };

  const stopDictation = () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
      recognitionRef.current = null;
    }
    if (recorderRef.current) {
      try {
        if (recorderRef.current.state === "recording") recorderRef.current.stop();
      } catch {}
      recorderRef.current = null;
    }
    if (recorderTimerRef.current) {
      clearTimeout(recorderTimerRef.current);
      recorderTimerRef.current = null;
    }
    setDictating(false);
    setDictationInterim("");
  };

  const startMediaDictation = async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia || typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
      setError("Voice dictation is not available in this browser.");
      return;
    }
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recorderStreamRef.current = stream;
      let mime = "";
      try {
        if (MediaRecorder.isTypeSupported("audio/webm")) mime = "audio/webm";
        else if (MediaRecorder.isTypeSupported("audio/mp4")) mime = "audio/mp4";
      } catch {}
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
      recorder.onstop = () => {
        if (recorderTimerRef.current) {
          clearTimeout(recorderTimerRef.current);
          recorderTimerRef.current = null;
        }
        try { stream.getTracks().forEach((track) => track.stop()); } catch {}
        recorderRef.current = null;
        recorderStreamRef.current = null;
        finishMediaDictation(chunks, mime);
      };
      recorder.start();
      setDictating(true);
      setDictationInterim("Listening...");
      recorderTimerRef.current = setTimeout(() => {
        try { if (recorder.state === "recording") recorder.stop(); } catch {}
      }, 20000);
    } catch {
      try { stream?.getTracks?.().forEach((track) => track.stop()); } catch {}
      setDictating(false);
      setDictationInterim("");
      setError("Microphone permission is needed for dictation.");
    }
  };

  const startDictation = () => {
    if (dictating || dictationBusy) return;
    setError("");
    setDictationInterim("");
    const SpeechRecognitionCtor = typeof window !== "undefined"
      ? (window.SpeechRecognition || window.webkitSpeechRecognition)
      : null;
    if (!SpeechRecognitionCtor) {
      startMediaDictation();
      return;
    }
    try {
      const recognition = new SpeechRecognitionCtor();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = "en-US";
      recognition.onstart = () => {
        setDictating(true);
        setDictationInterim("Listening...");
      };
      recognition.onresult = (event) => {
        let finalText = "";
        let interim = "";
        for (let i = event.resultIndex || 0; i < event.results.length; i += 1) {
          const piece = event.results[i]?.[0]?.transcript || "";
          if (event.results[i]?.isFinal) finalText += piece;
          else interim += piece;
        }
        if (finalText) setPrompt((prev) => appendDictationText(prev, finalText));
        setDictationInterim(interim.trim() || (finalText ? "" : "Listening..."));
      };
      recognition.onerror = (event) => {
        const msg = event?.error === "not-allowed"
          ? "Microphone permission is needed for dictation."
          : "Dictation could not capture audio.";
        setError(msg);
      };
      recognition.onend = () => {
        recognitionRef.current = null;
        setDictating(false);
        setDictationInterim("");
      };
      recognitionRef.current = recognition;
      recognition.start();
    } catch {
      recognitionRef.current = null;
      setDictating(false);
      setDictationInterim("");
      startMediaDictation();
    }
  };

  const surfaceLabel = shortSurface(section);
  const modeMeta = mode === "code"
    ? { title: "Code mode", hint: "Mini IDE lane", placeholder: "Ask Hermes for code: scaffold files, inspect a bundle, draft scripts, or prepare command notes..." }
    : { title: "Hermes Co-work", hint: "Everyday work", placeholder: "Ask Hermes Co-work to draft, research, analyze files, build docs, spreadsheets, slides, or coordinate app work..." };
  const accent = t.accent || "#3b82c4";
  const accent2 = t.accent2 || accent;
  const accentFg = t.accent_fg || "#ffffff";
  const panelBg = t.surface_raised || t.surface || "#fff";
  const text = t.text_primary || "#0f172a";
  const muted = t.text_secondary || t.text_muted || "#64748b";
  const border = t.border || "rgba(91,130,180,0.22)";
  const launcherActive = open || launcherHot;
  const viewportWidth = viewportBox.width || (typeof window === "undefined" ? 0 : (window.visualViewport?.width || window.innerWidth || 0));
  const viewportHeight = viewportBox.height || (typeof window === "undefined" ? 0 : (window.visualViewport?.height || window.innerHeight || 0));
  const trayPanelLayout = computeResidentTrayPanelLayout({ viewportWidth, viewportHeight, placement });
  const trayDensity = trayPanelLayout.density || "roomy";
  const trayCompact = trayDensity === "compact" || trayDensity === "micro";
  const trayMicro = trayDensity === "micro";
  const trayPanelWidth = Math.max(0, Math.round(trayPanelLayout.width || RESIDENT_TRAY_PANEL_MAX_WIDTH));
  const trayPanelMaxHeightPx = Math.max(0, Math.floor(trayPanelLayout.maxHeight || Math.max(0, viewportHeight - RESIDENT_TRAY_MARGIN * 2)));
  const trayPanelMaxHeight = `min(${trayPanelMaxHeightPx}px, calc(100dvh - ${RESIDENT_TRAY_MARGIN * 2}px - env(safe-area-inset-top) - env(safe-area-inset-bottom)))`;
  const bodyGap = trayMicro ? 6 : (trayCompact ? 7 : 8);
  const uploadHelp = trayMicro
    ? "Drop work here or choose files before starting a run."
    : trayCompact
      ? "Drop work here or choose files. Hermes reads docs, sheets, slides, images, code, and zipped app bundles."
      : "Drop work here or choose files. Hermes can read documents, spreadsheets, presentations, images, code files, and zipped app bundles before starting a run.";
  const downloadsEmptyCopy = trayMicro
    ? "Active runs and finished files appear here."
    : trayCompact
      ? "Active runs stay visible here. Generated files appear as soon as Hermes writes them."
      : "Active runs stay visible here while you submit more work. Generated documents and artifacts appear as direct downloads.";
  const dictationDisabled = busy || dictationBusy;
  const dictationLabel = dictating ? "Stop dictating Hermes request" : "Start dictating Hermes request";
  const canSubmit = !!prompt.trim() && !busy && !attachmentBusy;
  const canAttachMore = !busy && !attachmentBusy && attachments.length < HERMES_ATTACHMENT_MAX_FILES;
  const downloadsFeedMaxHeight = trayMicro ? 132 : (trayCompact ? 166 : 214);

  return (
    <div
      ref={rootRef}
      data-hermes-resident-tray="true"
      style={{
        position: "fixed",
        right: `calc(${placement.right}px + env(safe-area-inset-right))`,
        bottom: `calc(${placement.bottom}px + env(safe-area-inset-bottom))`,
        zIndex: open ? 870 : 260,
        fontFamily: "var(--cf-brand-font, Outfit, system-ui, sans-serif)",
        transition: "right 180ms ease, bottom 180ms ease",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
    >
      <style>{`
        @keyframes cfHermesTrayGoo {
          0%,100% { transform: translate3d(0,0,0) scale(1); border-radius: 43% 57% 50% 50%; }
          45% { transform: translate3d(1px,-2px,0) scale(1.045); border-radius: 58% 42% 47% 53%; }
        }
        @keyframes cfHermesTrayPulse {
          0%,100% { opacity: .32; transform: scale(.84); }
          50% { opacity: .78; transform: scale(1.16); }
        }
        @keyframes cfHermesTrayHalo {
          0%,100% { opacity: .3; transform: scale(.92); }
          50% { opacity: .58; transform: scale(1.08); }
        }
        .cfHermesTrayOrb {
          animation: cfHermesTrayGoo 5.6s ease-in-out infinite;
        }
        .cfHermesTrayPulse {
          animation: cfHermesTrayPulse 2.35s ease-in-out infinite;
        }
        .cfHermesTrayHalo {
          animation: cfHermesTrayHalo 3.8s ease-in-out infinite;
        }
        .cfHermesTrayMic[data-dictating="true"] {
          animation: cfHermesTrayMicPulse 1.25s ease-in-out infinite;
        }
        @keyframes cfHermesTrayMicPulse {
          0%,100% { box-shadow: 0 0 0 0 ${accent}55; }
          50% { box-shadow: 0 0 0 7px ${accent}00; }
        }
        @media (prefers-reduced-motion: reduce) {
          .cfHermesTrayOrb,
          .cfHermesTrayPulse,
          .cfHermesTrayHalo,
          .cfHermesTrayMic {
            animation: none !important;
          }
        }
      `}</style>
      {open && (
        <div
          onDragOver={(e) => { e.preventDefault(); if (canAttachMore) setDropHot(true); }}
          onDragLeave={(e) => { if (e.currentTarget === e.target) setDropHot(false); }}
          onDrop={async (e) => { e.preventDefault(); setDropHot(false); if (canAttachMore) await addAttachments(e.dataTransfer?.files || []); }}
          data-hermes-tray-panel-placement={trayPanelLayout.placement}
          data-hermes-tray-density={trayDensity}
          data-hermes-tray-emergency-overflow={trayPanelLayout.emergencyOverflow ? "true" : "false"}
          style={{
            position: "fixed",
            left: `calc(${Math.round(trayPanelLayout.left || RESIDENT_TRAY_MARGIN)}px + env(safe-area-inset-left))`,
            top: `calc(${Math.round(trayPanelLayout.top || RESIDENT_TRAY_MARGIN)}px + env(safe-area-inset-top))`,
            width: `min(${trayPanelWidth}px, calc(100vw - ${RESIDENT_TRAY_MARGIN * 2}px - env(safe-area-inset-left) - env(safe-area-inset-right)))`,
            maxWidth: `calc(100vw - ${RESIDENT_TRAY_MARGIN * 2}px - env(safe-area-inset-left) - env(safe-area-inset-right))`,
            maxHeight: trayPanelMaxHeight,
            background: panelBg,
            border: `1px solid ${dropHot ? accent : border}`,
            borderRadius: 12,
            boxShadow: "0 24px 80px rgba(15,23,42,.24)",
            overflowY: trayPanelLayout.overflowY || "visible",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div style={{ padding: trayMicro ? "9px 11px" : "12px 14px", borderBottom: `1px solid ${border}`, display: "flex", alignItems: "center", gap: trayMicro ? 8 : 10 }}>
            <div className="cfHermesTrayOrb" aria-hidden="true" style={{ width: trayMicro ? 28 : 32, height: trayMicro ? 28 : 32, borderRadius: "50%", position: "relative", background: `radial-gradient(circle at 34% 26%, ${accentFg} 0%, ${accent2} 32%, ${accent} 68%, ${border} 100%)`, boxShadow: `0 10px 28px ${accent}44`, flexShrink: 0, overflow: "hidden" }}>
              <div style={{ position: "absolute", inset: 6, border: `1px solid ${accentFg}`, borderRadius: "inherit", opacity: .52 }} />
              <div className="cfHermesTrayPulse" style={{ position: "absolute", width: 9, height: 9, right: 3, top: 5, borderRadius: "50%", background: accentFg }} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: text, lineHeight: 1.1 }}>Hermes orb</div>
              <div style={{ fontSize: 10, color: muted, marginTop: 2, fontFamily: "monospace" }}>{modeMeta.title} · {surfaceLabel} · {activeCount} active</div>
            </div>
            <button type="button" onClick={() => { if (typeof onOpenHermes === "function") onOpenHermes(); }} style={{ border: `1px solid ${border}`, background: "transparent", color: muted, borderRadius: 8, padding: "5px 8px", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Runs</button>
          </div>
          <div style={{ padding: trayMicro ? 10 : 12, display: "flex", flexDirection: "column", gap: bodyGap }}>
            <div role="tablist" aria-label="Hermes orb modes" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: trayMicro ? 5 : 6 }}>
              {[
                ["cowork", "Co-work", "docs + knowledge"],
                ["code", "Code", "friendly IDE"],
              ].map(([key, label, hint]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={mode === key}
                  onClick={() => setMode(key)}
                  style={{ textAlign: "left", border: `1px solid ${mode === key ? accent : border}`, borderRadius: 9, padding: trayMicro ? "7px 8px" : "8px 10px", background: mode === key ? `${accent}12` : (t.surface_alt || "rgba(255,255,255,.8)"), cursor: "pointer", fontFamily: "inherit", minHeight: 42 }}
                >
                  <div style={{ fontSize: 11, fontWeight: 800, color: mode === key ? accent : text, lineHeight: 1.1 }}>{label}</div>
                  {!trayMicro && <div style={{ fontSize: 9, color: muted, marginTop: 2 }}>{hint}</div>}
                </button>
              ))}
            </div>
            {mode === "code" && typeof onOpenCode === "function" && (
              <button
                type="button"
                onClick={() => { setOpen(false); onOpenCode(); }}
                style={{ border: `1px solid ${accent}`, background: `${accent}12`, color: accent, borderRadius: 9, padding: "8px 10px", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
              >
                Open Code mode <span style={{ fontWeight: 600, color: muted }}>· files, editor, preview, workspace download</span>
              </button>
            )}
            <div style={{ border: `1px dashed ${dropHot ? accent : border}`, borderRadius: 10, padding: trayMicro ? "7px 9px" : "9px 10px", background: dropHot ? `${accent}12` : (t.surface_alt || "rgba(255,255,255,.8)"), color: muted, fontSize: trayMicro ? 10.5 : 11, lineHeight: trayCompact ? 1.35 : 1.45 }}>
              {uploadHelp}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={HERMES_ATTACHMENT_ACCEPT}
              style={{ display: "none" }}
              onChange={async (e) => {
                await addAttachments(e.target.files || []);
                e.target.value = "";
              }}
            />
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={modeMeta.placeholder}
              rows={trayCompact ? 2 : 3}
              style={{ width: "100%", boxSizing: "border-box", resize: "vertical", minHeight: trayMicro ? 54 : (trayCompact ? 62 : 74), maxHeight: trayMicro ? 112 : 180, background: t.surface_alt || "rgba(255,255,255,.8)", border: `1px solid ${border}`, borderRadius: 9, padding: trayMicro ? "8px 9px" : "9px 10px", color: text, fontSize: 13, fontFamily: "inherit", outline: "none" }}
            />
            {(dictationInterim || dictationBusy) && (
              <div role="status" aria-live="polite" style={{ fontSize: 10, color: dictating ? accent : muted, fontFamily: "monospace", minHeight: 13 }}>
                {dictationBusy ? "Transcribing..." : dictationInterim}
              </div>
            )}
            {attachments.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {attachments.map((att) => (
                  <div key={att.id} style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 8px", borderRadius: 8, background: t.surface_alt || "rgba(255,255,255,.8)", border: `1px solid ${border}` }}>
                    <span aria-hidden="true" style={{ width: 16, height: 16, color: accent, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {/^image\//i.test(att.mime) ? (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="16" rx="2" />
                          <circle cx="8.5" cy="9" r="1.5" />
                          <path d="M21 15l-5-5L5 20" />
                        </svg>
                      ) : (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21.4 11.6l-8.5 8.5a6 6 0 0 1-8.5-8.5l9-9a4 4 0 0 1 5.7 5.7l-9 9a2 2 0 0 1-2.8-2.8l8.5-8.5" />
                        </svg>
                      )}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, color: text, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.name}</div>
                      <div style={{ fontSize: 9, color: muted, fontFamily: "monospace" }}>{formatAttachmentBytes(att.size)} · {att.extraction || "stored"}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(att.id)}
                      aria-label={"Remove " + att.name}
                      title={"Remove " + att.name}
                      style={{ width: 24, height: 24, borderRadius: 7, border: `1px solid ${border}`, background: "transparent", color: muted, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                        <path d="M18 6L6 18" />
                        <path d="M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
            {error && <div style={{ color: t.danger || "#dc2626", fontSize: 11, fontFamily: "monospace" }}>{error}</div>}
            <div style={{ border: `1px solid ${border}`, borderRadius: 10, padding: trayMicro ? "7px 8px" : "8px 9px", background: t.surface_alt || "rgba(255,255,255,.8)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: trayMicro ? 4 : 6 }}>
                <div style={{ fontSize: 11, fontWeight: 900, color: text }}>Runs & files</div>
                <div style={{ marginLeft: "auto", fontSize: 9, color: muted, fontFamily: "monospace" }}>{downloadsLoading ? "checking..." : "live + recent"}</div>
              </div>
              {downloadsError ? (
                <div role="status" aria-live="polite" style={{ fontSize: 10.5, color: t.danger || "#dc2626", lineHeight: trayCompact ? 1.3 : 1.45 }}>
                  Run status is temporarily unavailable. Open Runs for full history.
                </div>
              ) : downloadRuns.length === 0 ? (
                <div style={{ fontSize: 10.5, color: muted, lineHeight: trayCompact ? 1.3 : 1.45 }}>
                  {downloadsLoading ? "Checking active runs and recent documents..." : downloadsEmptyCopy}
                </div>
              ) : (
                <div
                  role="list"
                  aria-label="Hermes live and recent runs"
                  data-hermes-runs-feed="true"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: trayMicro ? 4 : 5,
                    maxHeight: downloadsFeedMaxHeight,
                    overflowY: "auto",
                    overscrollBehavior: "contain",
                    WebkitOverflowScrolling: "touch",
                    paddingRight: downloadRuns.length > 2 ? 3 : 0,
                  }}
                >
                  {downloadRuns.map(({ run, files, totalFiles }) => {
                    const terminal = isTerminalHermesRun(run);
                    const statusColor = terminal
                      ? (run.status === "done" ? (t.success || "#16a34a") : (t.warn || "#d97706"))
                      : accent;
                    const progress = runProgressMeta(run);
                    const shownFiles = files.slice(0, trayMicro ? 1 : 2);
                    const deleteBusy = deleteRunBusy === run.id;
                    const deleteDisabled = deleteBusy || !terminal;
                    const deleteLabel = deleteBusy ? "..." : (trayMicro ? "Del" : "Delete");
                    return (
                      <div key={run.id} role="listitem" style={{ borderTop: `1px solid ${border}`, paddingTop: 6 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                          <span style={{ border: `1px solid ${statusColor}`, color: statusColor, background: `${statusColor}12`, borderRadius: 999, padding: "2px 6px", fontSize: 8.5, fontWeight: 900, fontFamily: "monospace", flexShrink: 0 }}>
                            {runStatusLabel(run.status)}
                          </span>
                          <button type="button" onClick={() => { if (typeof onOpenHermes === "function") onOpenHermes(run.id); try { window.location.hash = "#hermes/run/" + encodeURIComponent(run.id); } catch {} }} style={{ flex: 1, minWidth: 0, border: "none", background: "transparent", color: text, padding: 0, fontSize: 10.5, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                            {run.goal || run.title || "Hermes run"}
                          </button>
                          {terminal && !trayMicro && (
                            <button type="button" onClick={() => downloadRunZip(run.id)} disabled={downloadBusy === "zip:" + run.id} style={{ border: `1px solid ${accent}`, background: "transparent", color: accent, borderRadius: 7, padding: "3px 7px", fontSize: 10, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>{downloadBusy === "zip:" + run.id ? "..." : "Zip"}</button>
                          )}
                          <button
                            type="button"
                            onClick={() => deleteRun(run)}
                            disabled={deleteDisabled}
                            aria-label={"Delete Hermes run " + (run.goal || run.title || run.id)}
                            title={terminal ? "Delete this finished run and its files." : "Runs can be deleted after they finish or are cancelled."}
                            style={{ border: `1px solid ${terminal ? (t.danger || "#dc2626") : border}`, background: "transparent", color: terminal ? (t.danger || "#dc2626") : muted, borderRadius: 7, padding: trayMicro ? "3px 5px" : "3px 7px", fontSize: 10, fontWeight: 800, cursor: deleteDisabled ? "default" : "pointer", fontFamily: "inherit", opacity: deleteDisabled && !deleteBusy ? .48 : 1 }}
                          >
                            {deleteLabel}
                          </button>
                        </div>
                        <div style={{ marginTop: 3, fontSize: 9, color: muted, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {progress || (terminal ? "finished" : "working")}
                        </div>
                        {deleteRunErrors[run.id] && (
                          <div role="status" aria-live="polite" style={{ marginTop: 4, fontSize: 10, color: t.danger || "#dc2626", lineHeight: 1.3 }}>
                            {deleteRunErrors[run.id]}
                          </div>
                        )}
                        {shownFiles.length > 0 ? (
                          shownFiles.map((file) => {
                            const fileKey = run.id + ":" + (file.id || file.path);
                            return (
                              <button key={file.id || file.path} type="button" onClick={() => downloadRunFile(run.id, file)} disabled={downloadBusy === fileKey} aria-label={runFileDownloadLabel(file) + " " + runFileName(file)} style={{ width: "100%", marginTop: 4, border: `1px solid ${border}`, background: panelBg, color: muted, borderRadius: 7, padding: "5px 7px", display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                                <span aria-hidden="true">↓</span>
                                <span style={{ color: accent, fontSize: 10, fontWeight: 900, whiteSpace: "nowrap" }}>{runFileDownloadLabel(file)}</span>
                                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 10.5 }}>{runFileName(file)}</span>
                                <span style={{ fontSize: 9, fontFamily: "monospace" }}>{downloadBusy === fileKey ? "..." : formatAttachmentBytes(Number(file.size_bytes))}</span>
                              </button>
                            );
                          })
                        ) : (
                          <div style={{ marginTop: 4, fontSize: 10, color: muted, lineHeight: 1.3 }}>
                            {isActiveHermesRun(run)
                              ? "Working. Generated documents will appear here when Hermes writes them."
                              : totalFiles > 0
                                ? "No generated document found yet. Source uploads are kept in Runs."
                                : "No downloadable document was produced for this run."}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: trayMicro ? 6 : 8, alignItems: "center" }}>
              <button
                type="button"
                className="cfHermesTrayMic"
                data-dictating={dictating ? "true" : "false"}
                aria-label={dictationLabel}
                title={dictationLabel}
                disabled={dictationDisabled}
                onClick={dictating ? stopDictation : startDictation}
                style={{ width: 42, height: 42, borderRadius: 12, border: `1px solid ${dictating ? accent : border}`, background: dictating ? `${accent}18` : (t.surface_alt || "transparent"), color: dictating ? accent : muted, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: dictationDisabled ? "default" : "pointer", opacity: dictationDisabled ? .56 : 1, flexShrink: 0 }}
              >
                {dictationBusy ? (
                  <span aria-hidden="true" style={{ fontSize: 15, fontWeight: 800 }}>...</span>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="9" y="3" width="6" height="11" rx="3" />
                    <path d="M5 11a7 7 0 0 0 14 0" />
                    <path d="M12 18v3" />
                    <path d="M8 21h8" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                disabled={!canAttachMore}
                onClick={() => fileInputRef.current?.click()}
                title="Attach PDFs, Word documents, PowerPoint files, spreadsheets, text/code files, zip bundles, and photos for Hermes to use."
                style={{ border: `1px solid ${border}`, borderRadius: 9, background: t.surface_alt || "transparent", color: muted, padding: "9px 10px", fontSize: 12, fontWeight: 800, cursor: canAttachMore ? "pointer" : "default", fontFamily: "inherit", whiteSpace: "nowrap", opacity: attachmentBusy ? .62 : 1 }}
              >
                {attachmentBusy ? "Reading..." : "Choose Files"}
              </button>
              <button type="button" disabled={!canSubmit} onClick={launch}
                style={{ flex: 1, border: "none", borderRadius: 9, background: !canSubmit ? border : `linear-gradient(135deg, ${accent}, ${accent2})`, color: accentFg, padding: "9px 12px", fontSize: 12, fontWeight: 800, cursor: !canSubmit ? "default" : "pointer", fontFamily: "inherit", minWidth: 120 }}>
                {busy ? "Submitting..." : "Submit to Hermes"}
              </button>
              <button type="button" onClick={() => setOpen(false)} style={{ border: `1px solid ${border}`, background: "transparent", color: muted, borderRadius: 9, padding: "9px 10px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Close</button>
            </div>
            {typeof onTalkToMe === "function" && (
              <button
                type="button"
                onClick={() => { setOpen(false); onTalkToMe(); }}
                title="Talk to Me -- hands-free voice conversation"
                style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", border: `1px solid ${accent}`, background: `${accent}12`, color: accent, borderRadius: 9, padding: "9px 12px", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                </svg>
                Talk to Me
              </button>
            )}
          </div>
        </div>
      )}
      <button
        type="button"
        aria-label="Open Hermes orb"
        aria-expanded={open}
        data-tooltip="Open Hermes orb"
        onMouseEnter={() => setLauncherHot(true)}
        onMouseLeave={() => setLauncherHot(false)}
        onFocus={() => setLauncherHot(true)}
        onBlur={() => setLauncherHot(false)}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: RESIDENT_TRAY_SIZE,
          height: RESIDENT_TRAY_SIZE,
          borderRadius: "50%",
          border: "none",
          background: "transparent",
          color: accentFg,
          boxShadow: launcherActive ? `0 18px 48px ${accent}48` : `0 10px 30px ${accent}30`,
          cursor: "pointer",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: launcherActive ? 1 : 0.72,
          transform: launcherActive ? "scale(1)" : "scale(.94)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          transition: "opacity 160ms ease, transform 160ms ease, box-shadow 160ms ease",
        }}
      >
        <span className="cfHermesTrayHalo" aria-hidden="true" style={{ position: "absolute", inset: -7, borderRadius: "50%", background: `radial-gradient(circle, ${accent}28 0%, ${accent}14 45%, transparent 72%)`, opacity: launcherActive ? .82 : .46 }} />
        <span className="cfHermesTrayOrb" aria-hidden="true" style={{ position: "absolute", inset: 0, borderRadius: "50%", background: `radial-gradient(circle at 34% 25%, ${accentFg} 0%, ${accent2} 28%, ${accent} 62%, ${border} 100%)`, boxShadow: `inset 0 0 0 1px ${accentFg}33, inset -10px -12px 24px rgba(15,23,42,.18)`, overflow: "hidden" }}>
          <span style={{ position: "absolute", inset: 10, border: `1px solid ${accentFg}`, borderRadius: "inherit", opacity: .46 }} />
          <span className="cfHermesTrayPulse" style={{ position: "absolute", width: 13, height: 13, right: 7, top: 8, borderRadius: "50%", background: accentFg }} />
          <span style={{ position: "absolute", width: 18, height: 18, left: 12, top: 10, borderRadius: "50%", background: "rgba(255,255,255,.42)", filter: "blur(2px)" }} />
        </span>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" aria-hidden="true" style={{ position: "relative", zIndex: 1, filter: "drop-shadow(0 1px 4px rgba(15,23,42,.32))" }}>
          <path d="M12 3l7 4v5c0 4.8-3 7.7-7 9-4-1.3-7-4.2-7-9V7l7-4z" />
          <path d="M9 12l2 2 4-5" />
        </svg>
        {activeCount > 0 && <span style={{ position: "absolute", right: -2, top: -2, minWidth: 18, height: 18, borderRadius: 10, background: t.success || "#16a34a", color: "#fff", border: "2px solid " + panelBg, fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>{activeCount}</span>}
      </button>
    </div>
  );
}

export { prepareHermesAttachment, HERMES_ATTACHMENT_EXTENSIONS, HERMES_ATTACHMENT_MAX_FILES, formatAttachmentBytes };
